import { useCallback, useState } from "react"
import { Alert, Box, CircularProgress, Tab, Tabs, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { ERROR, OK } from "helpers/data-statuses"
import { useAnalytics } from "connected-components/analytics"
import { SKILL_EVENTS } from "connected-components/skills/analytics/events"
import type { SkillCatalogSkill } from "connected-components/skills/types"
import { useSkillFiles } from "resources/skills"
import { SkillFileTree } from "./skill-file-tree"
import { SkillFileViewer } from "./skill-file-viewer"
import type { SkillsPageLayout } from "./skills-page-content"

const FILES_ONE_COLUMN_SX = { display: "flex", flexDirection: "column", gap: 2 } satisfies SxProps<Theme>
const FILES_TWO_COLUMN_SX = { display: "grid", gridTemplateColumns: "minmax(180px, 1fr) minmax(0, 3fr)", gap: 3, alignItems: "start" } satisfies SxProps<Theme>
const NOTICE_SX = { color: "text.secondary", py: 1.5 } satisfies SxProps<Theme>
const LOADING_SX = { display: "flex", justifyContent: "center", p: 2 } satisfies SxProps<Theme>
const PANEL_SX = { display: "flex", flexDirection: "column", gap: 2, minWidth: 0 } satisfies SxProps<Theme>

const DEFAULT_FILE = "SKILL.md"
const FILES_TAB_ID = "kelly-skill-files-tab"
const FILES_PANEL_ID = "kelly-skill-files-panel"

export interface SkillFilesTabProps {
  skill: SkillCatalogSkill
  layout: SkillsPageLayout
}

export function SkillFilesTab({ skill, layout }: SkillFilesTabProps) {
  const { status, files } = useSkillFiles(skill.tier, skill.id)
  const analytics = useAnalytics()
  const [selectedPath, setSelectedPath] = useState<string | null>(null)

  const defaultPath = files.find((file) => file.path === DEFAULT_FILE)?.path ?? files[0]?.path ?? null
  const activePath = selectedPath ?? defaultPath
  const activeFile = files.find((file) => file.path === activePath) ?? null

  const onSelectFile = useCallback((path: string) => {
    analytics.track(SKILL_EVENTS.SKILL_FILE_VIEWED, { skillId: skill.id, path })
    setSelectedPath(path)
  }, [analytics, skill.id])

  return (
    <>
      <Tabs value="files" aria-label="Skill detail">
        <Tab value="files" label="Files" id={FILES_TAB_ID} aria-controls={FILES_PANEL_ID} />
      </Tabs>
      <Box role="tabpanel" id={FILES_PANEL_ID} aria-labelledby={FILES_TAB_ID} sx={PANEL_SX}>
        {status === ERROR && <Alert severity="error">The files for {skill.name} did not load. Try again later.</Alert>}
        {status !== ERROR && status !== OK && <Box sx={LOADING_SX}><CircularProgress size={20} aria-label="Loading files" /></Box>}
        {status === OK && files.length === 0 && (
          <Typography sx={NOTICE_SX} data-testid="kelly-skill-files-empty">This skill&apos;s bundle has no files to show.</Typography>
        )}
        {status === OK && files.length > 0 && (
          <Box sx={layout === "two-column" ? FILES_TWO_COLUMN_SX : FILES_ONE_COLUMN_SX}>
            <SkillFileTree files={files} selectedPath={activePath} onSelect={onSelectFile} />
            {activeFile && <SkillFileViewer key={activeFile.path} tier={skill.tier} skillId={skill.id} file={activeFile} />}
          </Box>
        )}
      </Box>
    </>
  )
}
